// Status vocabulary: the badge, the insight verdict, the inline notice, and
// the empty state. Every state carries an icon with its own shape plus a
// word, so meaning never rests on colour. Status surfaces use the status
// surface token with its status border on all four sides; never a stripe.
import { useState, type ReactNode } from "react";
import { AnimatePresence, motion } from "motion/react";
import { ConsoleGlyph, type ConsoleGlyphName } from "./icons";
import { useEnterMotion } from "./motion";

export type BadgeTone = "neutral" | "info" | "success" | "warning" | "danger";

export interface BadgeProps {
  tone?: BadgeTone;
  /** Optional leading glyph (16). */
  icon?: ConsoleGlyphName;
  children: ReactNode;
}

/** Text label, pill, 13/500. Never a bare dot. */
export function Badge({ tone = "neutral", icon, children }: BadgeProps) {
  return (
    <span className="con-badge" data-tone={tone}>
      {icon ? <ConsoleGlyph name={icon} size={16} /> : null}
      <span>{children}</span>
    </span>
  );
}

/** The three Insight verdicts. */
export type Verdict = "good" | "watch" | "attention";

export const VERDICT_LABEL: Record<Verdict, string> = {
  good: "Berjalan baik",
  watch: "Perlu dipantau",
  attention: "Perlu tindakan",
};

const VERDICT_TONE: Record<Verdict, BadgeTone> = { good: "success", watch: "warning", attention: "danger" };
const VERDICT_ICON: Record<Verdict, ConsoleGlyphName> = { good: "good", watch: "watch", attention: "attention" };

export interface VerdictBadgeProps {
  verdict: Verdict;
  /** Override the default wording; it must still name the state. */
  label?: string;
}

/** good, watch, attention: a distinct icon shape plus the verdict in words. */
export function VerdictBadge({ verdict, label }: VerdictBadgeProps) {
  return (
    <span className="con-badge" data-tone={VERDICT_TONE[verdict]} data-verdict={verdict}>
      <ConsoleGlyph name={VERDICT_ICON[verdict]} size={16} />
      <span>{label ?? VERDICT_LABEL[verdict]}</span>
    </span>
  );
}

export type NoticeTone = "info" | "success" | "warning" | "danger";

const NOTICE_ICON: Record<NoticeTone, ConsoleGlyphName> = { info: "info", success: "good", warning: "warning", danger: "attention" };

export interface NoticeProps {
  tone?: NoticeTone;
  /** Names the state: "Data diperbarui", "Unggahan gagal". */
  title: string;
  children?: ReactNode;
  /** One secondary action (a button or link). */
  action?: ReactNode;
  /** Shows a close button; called after the notice has left. */
  onDismiss?: () => void;
  /** Accessible name of the close button. */
  dismissLabel?: string;
}

/**
 * Inline notification at the top of the region it describes. Danger is
 * role="alert"; the rest are role="status".
 */
export function Notice({ tone = "info", title, children, action, onDismiss, dismissLabel = "Tutup pemberitahuan" }: NoticeProps) {
  const [open, setOpen] = useState(true);
  const enter = useEnterMotion();
  return (
    <AnimatePresence onExitComplete={onDismiss}>
      {open ? (
        <motion.div className="con-notice" data-tone={tone} role={tone === "danger" ? "alert" : "status"} {...enter}>
          <span className="con-notice-icon">
            <ConsoleGlyph name={NOTICE_ICON[tone]} size={20} />
          </span>
          <div className="con-notice-text">
            <p className="con-notice-title">{title}</p>
            {children ? <div className="con-notice-body">{children}</div> : null}
            {action ? <div className="con-notice-action">{action}</div> : null}
          </div>
          {onDismiss ? (
            <button type="button" className="jal-icon-btn con-notice-close" aria-label={dismissLabel} onClick={() => setOpen(false)}>
              <ConsoleGlyph name="close" size={20} />
            </button>
          ) : null}
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

export interface EmptyStateProps {
  icon?: ConsoleGlyphName;
  title: string;
  /** One sentence: why it is empty and what to do. */
  children: ReactNode;
  /** One action. */
  action?: ReactNode;
}

/** Fills its region so it never collapses. */
export function EmptyState({ icon = "file", title, children, action }: EmptyStateProps) {
  return (
    <div className="con-empty">
      <span className="con-empty-icon">
        <ConsoleGlyph name={icon} size={24} />
      </span>
      <p className="con-empty-title">{title}</p>
      <p className="con-empty-text">{children}</p>
      {action ? <div className="con-empty-action">{action}</div> : null}
    </div>
  );
}

export interface DisclosureProps {
  /** What is inside, named as a thing, e.g. "Rincian metrik". */
  title: string;
  /** One short line under the title, optional. */
  description?: ReactNode;
  children: ReactNode;
}

/**
 * Detail a reader asks for: the prose, formulas, and document quotes behind
 * a screen, closed by default so the screen reads as its visuals. Native
 * details and summary, so it works with no script and is one tab stop; the
 * marker is a glyph in its own reserved lane, never a drawn triangle.
 */
export function Disclosure({ title, description, children }: DisclosureProps) {
  return (
    <details className="con-disclosure">
      <summary className="con-disclosure-summary">
        <span className="con-disclosure-text">
          <span className="con-disclosure-title">{title}</span>
          {description ? <span className="con-disclosure-description">{description}</span> : null}
        </span>
        <span className="con-disclosure-marker">
          <ConsoleGlyph name="down" size={20} />
        </span>
      </summary>
      <div className="con-disclosure-body">{children}</div>
    </details>
  );
}
